import { supabase } from './supabaseClient';

// --- Crédits Utilisateur ---
export const getUserCredits = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('credits')
    .eq('id', userId)
    .single();

  if (error) {
    console.error('Erreur récupération crédits (profiles):', error);
    return 0;
  }

  return data?.credits || 0;
};

// --- Catégories ---

// Ajout du paramètre competitionId pour filtrer les catégories du tournoi choisi
export async function fetchCategories(competitionId) {
  const { data, error } = await supabase
    .from('categories')
    .select(`
      *,
      athletes:category_members(
        athlete:athletes(*)
      )
    `)
    .eq('competition_id', competitionId) // <--- Ajout du filtre
    .order('name', { ascending: true });
  
  if (error) throw error;

  // Ta logique de nettoyage reste identique
  return data.map(cat => ({
    ...cat,
    athletes: cat.athletes.map(link => link.athlete)
  }));
}

export async function createCategory(category) {
  // category contient déjà { name, competition_id ... }
  const { data, error } = await supabase
    .from('categories')
    .insert([category])
    .select();
  
  if (error) throw error;
  return data[0];
}

export async function updateCategoryApi(categoryId, name) {
    const { data, error } = await supabase
        .from('categories')
        .update({ name })
        .eq('id', categoryId)
        .select()
        .single();

    if (error) throw error;
    return data;
}

// --- Athlètes ---

export async function addAthleteToCategory(categoryId, athlete, competitionId) {
  // 1. On crée d'abord l'athlète dans la table athletes
  const { data: newAthlete, error: athleteError } = await supabase
    .from('athletes')
    .insert([{ 
        name: athlete.name, 
        club: athlete.club,
        competition_id: competitionId // On ajoute l'ID de compétition
    }])
    .select();

  if (athleteError) throw athleteError;

  // 2. On le lie à la catégorie dans category_members
  const { error: linkError } = await supabase
    .from('category_members')
    .insert([{ category_id: categoryId, athlete_id: newAthlete[0].id }]);

  if (linkError) {
    // Si on n'a pas pu lier l'athlète, on tente de le supprimer pour éviter de polluer la table athletes
    await supabase.from('athletes').delete().eq('id', newAthlete[0].id);
    throw linkError;
  }
  return newAthlete[0];
}

export const deleteAthlete = async (categoryId, athleteId) => {
  // 1. Supprimer le lien dans category_members
  const { error: linkError } = await supabase
    .from('category_members')
    .delete()
    .eq('category_id', categoryId)
    .eq('athlete_id', athleteId);

  if (linkError) throw linkError;

  // 2. Supprimer l'athlète lui-même de la table athletes
  const { error: athleteError } = await supabase
    .from('athletes')
    .delete()
    .eq('id', athleteId);

  if (athleteError) {
      console.warn("L'athlète a été retiré de la catégorie mais n'a pas pu être supprimé de la table globale athletes:", athleteError);
  }

  return { status: 'success' };
};

export async function updateAthleteSeedApi(athleteId, seed) {
    const { data, error } = await supabase
        .from('athletes')
        .update({ seed: seed })
        .eq('id', athleteId)
        .select()
        .single();
    if (error) throw error;
    return data;
}

export async function updateAthleteApi(athleteId, details) {
    const { data, error } = await supabase
        .from('athletes')
        .update({
            name: details.name,
            club: details.club
        })
        .eq('id', athleteId)
        .select()
        .single();

    if (error) throw error;
    return data;
}

// --- Matchs & Plateau ---

/**
 * Sauvegarde tous les matchs générés en nettoyant les colonnes superflues
 */
export async function saveMatchesApi(matches, competitionId) {
    // 1. On nettoie les objets pour ne garder que ce que Supabase accepte
    const cleanMatches = matches.map(m => ({
        id: m.id,
        competition_id: competitionId,
        category_id: m.category_id,
        athlete1_id: m.athlete1_id,
        athlete2_id: m.athlete2_id,
        ring_id: m.ring_id,
        category_name: m.category_name,
        poule_label: m.poule_label || m.label, 
        match_ref: m.match_ref,
        placeholder1: m.placeholder1,
        placeholder2: m.placeholder2,
        source_match_id: m.source_match_id, // NOUVELLE COLONNE SQL
        source_match_id2: m.source_match_id2, // NOUVELLE COLONNE SQL
        type: m.type
    }));

    // 2. Insertion en base
    const { data, error } = await supabase
        .from('matches')
        .insert(cleanMatches)
        .select();

    if (error) throw error;
    return data;
}

export const fetchOrder = async (competitionId) => {
    const { data, error } = await supabase
        .from('matches')
        .select(`
            *,
            athlete1:athletes!athlete1_id(nom, prenom, club),
            athlete2:athletes!athlete2_id(nom, prenom, club)
        `)
        .eq('competition_id', competitionId)
        // On trie par match_ref pour que l'ordre 1, 2, 3 soit respecté au chargement
        .order('match_ref', { ascending: true });

    if (error) throw error;
    return data;
};

// --- Settings ---

export async function fetchSettings(competitionId) {
  // On ne prend plus "la première ligne", mais la ligne du tournoi choisi
  const { data, error } = await supabase
    .from('competitions')
    .select('format_config')
    .eq('id', competitionId) // <--- Ajout du filtre
    .single();

  if (error) {
    console.error("Erreur fetchSettings:", error);
    return { format_config: {} };
  }
  return data;
}

// Mise à jour des réglages pour LA compétition en cours
export async function saveSettingsApi(competitionId, settings) {
  const { error } = await supabase
    .from('competitions')
    .update({ format_config: settings })
    .eq('id', competitionId); // <--- Utilisation de l'ID passé

  if (error) throw error;
}

// --- Logique de Génération ---

export const generateOrderApi = async (nbRings, formatsConfig) => {
  console.warn("La génération doit maintenant être faite en JS côté Front-end");
  return { message: "Logique à implémenter en JS" };
};

export async function savePoulesApi(plateau) {
  const promises = [];

  Object.keys(plateau).forEach((ringKey) => {
    const ringNumber = parseInt(ringKey.replace("ring", ""));
    
    plateau[ringKey].forEach((poule, index) => {
      const promise = supabase
        .from('categories')
        .update({ 
          ring_id: ringNumber, 
          poule_order: index 
        })
        .eq('id', poule.category_id);
      
      promises.push(promise);
    });
  });

  const results = await Promise.all(promises);
  const error = results.find(r => r.error)?.error;
  if (error) throw error;
}

export const updateMatchWinnerApi = async (matchId, winnerId, nextMatchInfo = null, decisionData = null) => {
    // 1. Mise à jour du match actuel avec les détails de décision
    const updateData = { winner_id: winnerId };
    
    if (decisionData) {
        updateData.decision_type = decisionData.type;
        updateData.decision_detail = decisionData.detail;
        updateData.warnings_a1 = decisionData.warningsA1;
        updateData.warnings_a2 = decisionData.warningsA2;
    }

    const { error: matchError } = await supabase
        .from('matches')
        .update(updateData)
        .eq('id', matchId);

    if (matchError) throw matchError;

    // 2. Si on a un match suivant à mettre à jour (propagation)
    if (nextMatchInfo && nextMatchInfo.id) {
        const { error: nextError } = await supabase
            .from('matches')
            .update(nextMatchInfo.data)
            .eq('id', nextMatchInfo.id);
            
        if (nextError) throw nextError;
    }

    return { success: true };
};

export const generateAndSaveMatchesApi = async (matchesToSave, competitionId) => {
    // 1. On prépare les matchs SANS les IDs sources pour éviter les violations de clés étrangères lors de l'insert
    const initialMatches = matchesToSave.map(m => ({
        id: m.id || crypto.randomUUID(),
        competition_id: competitionId,
        category_id: m.category_id,
        athlete1_id: m.athlete1_id,
        athlete2_id: m.athlete2_id,
        ring_id: m.ring_id,
        category_name: m.category_name,
        poule_label: m.poule_label || m.label, 
        match_ref: m.match_ref,
        placeholder1: m.placeholder1,
        placeholder2: m.placeholder2,
        type: m.type,
        source_match_id: null,
        source_match_id2: null
    }));

    // 2. On supprime les anciens matchs de cette compétition uniquement
    await supabase.from('matches').delete().eq('competition_id', competitionId);

    // 3. ÉTAPE 1 : On insère les matchs "vides" de liens
    const { data: createdData, error: insertError } = await supabase
        .from('matches')
        .insert(initialMatches)
        .select();

    if (insertError) throw insertError;

    // 4. ÉTAPE 2 : On met à jour les liens de parenté (une fois que tous les IDs existent en base)
    const updatePromises = matchesToSave
        .filter(m => m.source_match_id || m.source_match_id2)
        .map(m => 
            supabase
                .from('matches')
                .update({
                    source_match_id: m.source_match_id,
                    source_match_id2: m.source_match_id2
                })
                .eq('id', m.id)
        );

    if (updatePromises.length > 0) {
        const updateResults = await Promise.all(updatePromises);
        const updateError = updateResults.find(r => r.error);
        if (updateError) console.error("Erreur lors de la mise à jour des liens parents:", updateError);
    }

    return createdData;
};

/**
 * Met à jour l'ordre (match_ref) et le ring de plusieurs matchs
 */
export async function updateMatchOrderApi(matches) {
    const promises = matches.map(m => 
        supabase
            .from('matches')
            .update({ 
                match_ref: m.match_ref,
                ring_id: m.ring_id
            })
            .eq('id', m.id)
    );

    const results = await Promise.all(promises);
    const error = results.find(r => r.error);
    if (error) throw error.error;
    return true;
}

export async function updateCompetitionDetails(competitionId, details) {
  const { data, error } = await supabase
    .from('competitions')
    .update({
      name: details.name,
      address: details.address,
      event_date: details.event_date,
      start_time: details.start_time,
      weigh_in_date: details.weigh_in_date,
      weigh_in_time: details.weigh_in_time,
      visibility: details.visibility,
      rings_count: details.rings_count
    })
    .eq('id', competitionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function notifyCoManagerApi(email, competitionName, addedBy) {
  // fetch direct avec la clé anon (évite le JWT ES256 injecté par supabase.functions.invoke)
  const res = await fetch('https://icaqghpgcoapikttehjg.supabase.co/functions/v1/notify-co-manager', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': 'sb_publishable_7nFecSfzP1uxlrIRKOwSJA_zIf8VfgR',
      'Authorization': 'Bearer sb_publishable_7nFecSfzP1uxlrIRKOwSJA_zIf8VfgR'
    },
    body: JSON.stringify({ email, competitionName, addedBy })
  });
  if (!res.ok) throw new Error('Échec de la notification');
}

export async function updateCoManagersApi(competitionId, coManagers) {
  const { data, error } = await supabase
    .from('competitions')
    .update({ co_managers: coManagers })
    .eq('id', competitionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateCoOwnersApi(competitionId, coOwners) {
  const { data, error } = await supabase
    .from('competitions')
    .update({ co_owners: coOwners })
    .eq('id', competitionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

// --- Compétitions Publiques ---

export async function fetchPublicCompetitions() {
  const { data, error } = await supabase
    .from('competitions')
    .select('*')
    .eq('visibility', 'public')
    .order('event_date', { ascending: false });

  if (error) throw error;
  return data;
}

// Récupérer la configuration du format d'une compétition
export async function fetchCompetitionSettings(compId) {
  const { data, error } = await supabase
    .from('competitions')
    .select('format_config')
    .eq('id', compId)
    .single();

  if (error) throw error;
  return data?.format_config || null;
}

// Récupérer tous les matchs d'une compétition
export async function fetchCompetitionMatches(compId) {
  const { data, error } = await supabase
    .from('matches')
    .select('*')
    .eq('competition_id', compId);

  if (error) throw error;
  return data || [];
}

// Récupérer tous les athlètes d'une compétition (Indispensable pour les noms dans le live)
export async function fetchCompetitionAthletes(compId) {
  const { data, error } = await supabase
    .from('athletes')
    .select('*')
    .eq('competition_id', compId);

  if (error) throw error;
  return data || [];
}

/**
 * Réinitialise les scores de tous les matchs d'une compétition
 * sans casser la structure des athlètes initiaux.
 */
export const resetCompetitionResultsApi = async (matches) => {
    const updates = matches.map(m => {
        // Logique de dépendance : si le slot attend un vainqueur (ex: "V10")
        const isDependent1 = m.placeholder1 && m.placeholder1.includes('V');
        const isDependent2 = m.placeholder2 && m.placeholder2.includes('V');

        return supabase
            .from('matches')
            .update({
                winner_id: null,
                // On remet à null seulement si l'athlète vient d'un match précédent
                athlete1_id: isDependent1 ? null : m.athlete1_id,
                athlete2_id: isDependent2 ? null : m.athlete2_id
            })
            .eq('id', m.id);
    });

    const results = await Promise.all(updates);
    
    // On vérifie si une erreur s'est glissée dans l'un des appels
    const error = results.find(r => r.error);
    if (error) throw new Error(error.error.message);

    return true;
};

/**
 * Met à jour la configuration des formats de poules d'une compétition
 */
export const updateCompetitionSettingsApi = async (compId, allSettings) => {
    const { data, error } = await supabase
        .from('competitions')
        .update({ format_config: allSettings })
        .eq('id', compId)
        .select() // Optionnel : pour récupérer la donnée mise à jour
        .single();

    if (error) throw error;
    return data;
};

/**
 * Alterne l'état de lancement (is_started) d'une compétition
 */
export const toggleCompetitionLiveApi = async (compId, newState) => {
    const { data, error } = await supabase
        .from('competitions')
        .update({ is_started: newState })
        .eq('id', compId)
        .select()
        .single();

    if (error) throw error;
    return data;
};

/**
 * Supprime définitivement une compétition
 */
export const deleteCompetitionApi = async (compId) => {
    const { error } = await supabase
        .from('competitions')
        .delete()
        .eq('id', compId);

    if (error) throw error;
    return true;
};

/**
 * Supprime proprement une catégorie et toutes ses dépendances
 */
export const deleteCategoryApi = async (categoryId) => {
    // 1. Récupérer les IDs des athlètes de cette catégorie pour les supprimer de la table athletes si nécessaire
    const { data: members } = await supabase
        .from('category_members')
        .select('athlete_id')
        .eq('category_id', categoryId);

    const athleteIds = members?.map(m => m.athlete_id) || [];

    // 2. Supprimer les matchs liés à cette catégorie
    await supabase.from('matches').delete().eq('category_id', categoryId);

    // 3. Supprimer les liens de membres de la catégorie
    await supabase.from('category_members').delete().eq('category_id', categoryId);

    // 4. Supprimer les athlètes eux-mêmes
    if (athleteIds.length > 0) {
        await supabase.from('athletes').delete().in('id', athleteIds);
    }

    // 5. Enfin, supprimer la catégorie elle-même
    const { error } = await supabase
        .from('categories')
        .delete()
        .eq('id', categoryId);

    if (error) throw error;
    return true;
};

// --- ADMINISTRATION ---

/**
 * Récupère la liste de tous les utilisateurs (pour les admins)
 */
export const adminFetchAllUsers = async () => {
    const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .order('updated_at', { ascending: false });

    if (error) throw error;
    return data;
};

/**
 * Récupère TOUTES les compétitions de la plateforme (pour les admins)
 */
export const adminFetchAllCompetitions = async () => {
    // On essaie d'abord avec le lien explicite (owner_id)
    // Si ça échoue (PGRST200), on récupère les compétitions et les profils séparément
    try {
        const { data, error } = await supabase
            .from('competitions')
            .select(`
                *,
                owner:profiles!owner_id(email, first_name, last_name)
            `)
            .order('created_at', { ascending: false });

        if (!error) return data;
        
        // Si erreur de relation (PGRST200), on fait en deux temps
        if (error.code === 'PGRST200') {
            const { data: comps } = await supabase.from('competitions').select('*').order('created_at', { ascending: false });
            const { data: profs } = await supabase.from('profiles').select('id, email, first_name, last_name');
            
            return comps.map(c => ({
                ...c,
                owner: profs.find(p => p.id === c.owner_id)
            }));
        }
        
        throw error;
    } catch (err) {
        console.error("Erreur adminFetchAllCompetitions:", err);
        throw err;
    }
};
